import { useEffect, useMemo, useState } from 'react'
import { XIcon } from 'lucide-react'
import {
  useListExternalMessagingChannelsQuery,
  type CustomAgentConfig,
  type CustomAgentNotifyOn,
  type NotificationRecipients
} from '@axilon-apps/domains-ontology-ui-state'

import { RecipientsField } from '../../../components/Notifications/RecipientsField'
import {
  isLikelyEmail,
  normalizeRecipients,
  sameRecipients
} from '../../../components/Notifications/recipients'

import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Section } from './config-detail-chrome'

const NO_TEAMS_CHANNEL = '__none__'

const NOTIFY_ON_OPTIONS: Array<{ value: CustomAgentNotifyOn; label: string }> = [
  { value: 'never', label: 'Never' },
  { value: 'failure', label: 'Failures' },
  { value: 'flagged', label: 'When flagged' },
  { value: 'always', label: 'Always' }
]

export interface CustomAgentNotificationsSectionProps {
  config: CustomAgentConfig
  disabled?: boolean
  busy?: boolean
  onSave: (patch: {
    notify_on: CustomAgentNotifyOn
    email_addresses: string[]
    recipients: NotificationRecipients
    teams_channel_id: string
  }) => Promise<void>
}

/**
 * Editable notify_on + optional email/Teams delivery. Outbound mail and the
 * stamped Teams channel only send when notify_on is not `never`.
 */
export function CustomAgentNotificationsSection({
  config,
  disabled,
  busy,
  onSave
}: CustomAgentNotificationsSectionProps) {
  const [notifyOn, setNotifyOn] = useState<CustomAgentNotifyOn>(config.notify_on)
  const [emails, setEmails] = useState<string[]>(config.email_addresses ?? [])
  const [emailDraft, setEmailDraft] = useState('')
  const [teamsChannelId, setTeamsChannelId] = useState(config.teams_channel_id ?? '')
  const [recipients, setRecipients] = useState(() => normalizeRecipients(config.recipients))

  useEffect(() => {
    setNotifyOn(config.notify_on)
    setEmails(config.email_addresses ?? [])
    setTeamsChannelId(config.teams_channel_id ?? '')
    setRecipients(normalizeRecipients(config.recipients))
    setEmailDraft('')
  }, [
    config.id,
    config.notify_on,
    config.email_addresses,
    config.teams_channel_id,
    config.recipients
  ])

  const { data: channels = [], isLoading: channelsLoading } = useListExternalMessagingChannelsQuery(
    {
      platform: 'teams',
      page_size: 500
    }
  )

  const channelOptions = useMemo(
    () =>
      channels.map((c) => ({
        id: c.id,
        label: c.channel_name
          ? `${c.channel_name}${c.team_name ? ` (${c.team_name})` : ''}`
          : c.channel_id
      })),
    [channels]
  )

  const emailsValid = emails.every(isLikelyEmail)
  const persistedEmails = config.email_addresses ?? []
  const persistedTeams = config.teams_channel_id ?? ''
  const dirty =
    notifyOn !== config.notify_on ||
    emails.join('\0') !== persistedEmails.join('\0') ||
    !sameRecipients(recipients, config.recipients) ||
    teamsChannelId !== persistedTeams

  const addEmailFromDraft = () => {
    const value = emailDraft.trim().replace(/,$/, '')
    if (!value) return
    setEmails((current) => (current.includes(value) ? current : [...current, value]))
    setEmailDraft('')
  }

  const handleSave = () => {
    if (!emailsValid || !dirty || busy || disabled) return
    void onSave({
      notify_on: notifyOn,
      email_addresses: emails,
      recipients,
      teams_channel_id: teamsChannelId
    })
  }

  return (
    <Section
      label="Notifications"
      hint="After a run finishes, this policy decides whether anyone hears about it. Findings land in the bell of the people below and, if they keep email on, their inbox. Other addresses and Teams are optional."
    >
      <div className="flex flex-col gap-4 rounded-xl border border-line bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="custom-agent-notify-on">When to notify</Label>
          <Select
            value={notifyOn}
            onValueChange={(val) => setNotifyOn(val as CustomAgentNotifyOn)}
            disabled={disabled || busy}
          >
            <SelectTrigger id="custom-agent-notify-on" className="w-full sm:max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NOTIFY_ON_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Who gets findings</Label>
          <RecipientsField
            value={recipients}
            onChange={setRecipients}
            disabled={disabled || busy}
            defaultLabel="operators (and everyone above them)"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="custom-agent-email">Other email addresses</Label>
          {emails.length > 0 && (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {emails.map((email) => (
                <li
                  key={email}
                  className="inline-flex max-w-full items-center gap-1 rounded border border-line bg-bg-elev/60 px-2 py-1 text-xs"
                >
                  <span className="truncate">{email}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    className="shrink-0 text-ink-4 hover:text-ink"
                    disabled={disabled || busy}
                    onClick={() => setEmails((current) => current.filter((e) => e !== email))}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Input
            id="custom-agent-email"
            value={emailDraft}
            disabled={disabled || busy}
            onChange={(e) => setEmailDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                e.preventDefault()
                addEmailFromDraft()
              }
            }}
            onBlur={addEmailFromDraft}
            placeholder="operator@example.com — press Enter to add"
          />
          {!emailsValid && (
            <p className="m-0 text-xs text-st-down">One or more email addresses look invalid.</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="custom-agent-teams">Teams channel</Label>
          <Select
            value={teamsChannelId || NO_TEAMS_CHANNEL}
            onValueChange={(val) => setTeamsChannelId(val === NO_TEAMS_CHANNEL ? '' : val)}
            disabled={disabled || busy}
          >
            <SelectTrigger id="custom-agent-teams" className="w-full sm:max-w-md">
              <SelectValue
                placeholder={channelsLoading ? 'Loading channels…' : 'No Teams channel'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TEAMS_CHANNEL}>No Teams channel</SelectItem>
              {channelOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {channelOptions.length === 0 && !channelsLoading && (
            <p className="m-0 text-xs text-ink-4">
              No connected Teams channels. Connect one under Settings → Integrations.
            </p>
          )}
        </div>

        <div>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={disabled || busy || !dirty || !emailsValid}
          >
            {busy ? 'Saving…' : 'Save notifications'}
          </Button>
        </div>
      </div>
    </Section>
  )
}
